import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Button,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import QrCodeIcon from "@mui/icons-material/QrCode2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { getPublicAssignments, getCourtStartTime, getPublishedPageConfig, setActiveGroupSlug, getActiveGroupSlug, getPublishedPolls, getPersonalMessage, getBallkidNameById, getCurrentMinuteSlot } from "../firebaseService";
import { QRCodeSVG } from "qrcode.react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import PollVote from "../components/PollVote";

const CollapsibleSection = ({ title, defaultOpen = false, color = "#90caf9", children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mt: 3 }}>
      <Button
        fullWidth
        onClick={() => setOpen(!open)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          color: "white",
          bgcolor: `${color}22`,
          border: `1px solid ${color}44`,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "1rem",
          py: 1.2,
          "&:hover": { bgcolor: `${color}33` },
        }}
      >
        {title}
      </Button>
      {open && <Box sx={{ mt: 1 }}>{children}</Box>}
    </Box>
  );
};

const BallkindPage = () => {
  const { id, groupSlug } = useParams();
  const [latestAssignment, setLatestAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crewTimes, setCrewTimes] = useState([]);
  const [config, setConfig] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [polls, setPolls] = useState([]);
  const [personalMessage, setPersonalMessage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [minuteSlot, setMinuteSlot] = useState(getCurrentMinuteSlot());

  useEffect(() => {
    // Setze Gruppen-Prefix fuer oeffentliche Seite
    if (groupSlug) {
      setActiveGroupSlug(groupSlug);
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const [pageConfig, assignments, loadedMsg, fullName] = await Promise.all([
          getPublishedPageConfig(),
          getPublicAssignments(id),
          getPersonalMessage(id),
          getBallkidNameById(id),
        ]);
        setConfig(pageConfig);
        setPersonalMessage(loadedMsg);
        setFirstName(fullName ? fullName.split(" ")[0] : "");

        if (assignments.length > 0) {
          const latest = assignments[assignments.length - 1];
          setLatestAssignment(latest);

          if (pageConfig.showCrewTimes) {
            const { startTime: initialStartTime, hasThreeCrews, isTurnusEnabled } =
              await getCourtStartTime(latest.day, latest.court);

            const totalCrews = hasThreeCrews ? 3 : 2;
            const crewOffsets = { A: 0, B: 1, C: 2 };

            if (initialStartTime) {
              const [initialHour, initialMinute] = initialStartTime.split(":").map(Number);
              const crewSchedule = [];
              const turnusMinutes = isTurnusEnabled ? 45 : 60;
              const interval = turnusMinutes * totalCrews;
              const startOffsetMinutes = crewOffsets[latest.crew] * turnusMinutes;

              let currentDate = new Date();
              currentDate.setHours(initialHour);
              currentDate.setMinutes(initialMinute + startOffsetMinutes);
              currentDate.setSeconds(0);
              currentDate.setMilliseconds(0);

              const endHour = 21;
              while (currentDate.getHours() < endHour) {
                const timeString = currentDate.toTimeString().slice(0, 5);
                crewSchedule.push(timeString);
                currentDate = new Date(currentDate.getTime() + interval * 60000);
              }
              setCrewTimes(crewSchedule);
            }
          }
        }

        // Abrechnungsinfo laden wenn veröffentlicht
        if (pageConfig.showBilling) {
          try {
            const billingCol = `${getActiveGroupSlug()}__billing_public`;
            const billingDoc = await getDoc(doc(db, billingCol, id));
            if (billingDoc.exists()) {
              setBillingInfo(billingDoc.data());
            }
          } catch (e) {
            // kein Billing vorhanden
          }
        }
        // Polls laden
        try {
          const publishedPolls = await getPublishedPolls();
          setPolls(publishedPolls.filter((p) => p.showOnMemberPage));
        } catch (e) {
          // keine Polls
        }
      } catch (error) {
        console.error("Fehler beim Laden der Daten:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, [id, groupSlug]);

  // Rotate QR code every minute
  useEffect(() => {
    if (!showQR) return;
    const interval = setInterval(() => setMinuteSlot(getCurrentMinuteSlot()), 60000);
    return () => clearInterval(interval);
  }, [showQR]);

  // QR code value (ID + timestamp, base64 encoded to obscure content)
  const qrValue = useMemo(() => {
    if (!id) return null;
    const payload = JSON.stringify({ id, ts: minuteSlot });
    return "BK:" + btoa(payload);
  }, [id, minuteSlot]);

  if (loading || !config) {
    return <CircularProgress style={{ display: "block", margin: "50px auto" }} />;
  }

  return (
    <Box sx={{ bgcolor: "#191f34", minHeight: "100vh" }}>
      {/* Scrolling Banner */}
      {config.sliderEnabled && config.sliderText && (
        <Box
          sx={{
            width: "100%",
            bgcolor: config.sliderBgColor || "#ffca28",
            color: "#000",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            overflow: "hidden",
            position: "sticky",
            top: 0,
            zIndex: 999,
          }}
        >
          <Box
            component={config.sliderLink ? "a" : "span"}
            href={config.sliderLink || undefined}
            sx={{ display: "inline-block", animation: "scroll 15s linear infinite", px: 2, textDecoration: "none", color: "inherit" }}
          >
            {config.sliderText}
          </Box>
          <style>{`@keyframes scroll { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }`}</style>
        </Box>
      )}

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {firstName && (
          <Typography variant="h4" align="center" color="white" gutterBottom>
            Hallo {firstName}
          </Typography>
        )}

        {personalMessage && (
          <Paper
            elevation={8}
            sx={{
              mt: 2,
              mb: 3,
              py: 2.5,
              px: 3,
              bgcolor: "#ffca28",
              color: "#1a1a1a",
              border: "3px solid #ff6f00",
              borderRadius: 2,
              boxShadow: "0 0 0 4px rgba(255,202,40,0.25), 0 4px 20px rgba(0,0,0,0.35)",
              animation: "personalMsgPulse 2s ease-in-out infinite",
              "@keyframes personalMsgPulse": {
                "0%, 100%": {
                  boxShadow:
                    "0 0 0 4px rgba(255,202,40,0.25), 0 4px 20px rgba(0,0,0,0.35)",
                },
                "50%": {
                  boxShadow:
                    "0 0 0 8px rgba(255,202,40,0.45), 0 4px 28px rgba(255,111,0,0.55)",
                },
              },
            }}
          >
            <Typography
              variant="overline"
              align="center"
              sx={{
                display: "block",
                fontWeight: 800,
                letterSpacing: "0.15em",
                color: "#b71c1c",
                mb: 0.5,
              }}
            >
              Persoenliche Nachricht
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ whiteSpace: "pre-line", fontWeight: 600, color: "#1a1a1a" }}
            >
              {personalMessage}
            </Typography>
          </Paper>
        )}

        {/* Self Check-In QR Code */}
        {config.selfCheckInEnabled && (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<QrCodeIcon />}
              endIcon={showQR ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowQR(!showQR)}
              sx={{
                background: showQR
                  ? "linear-gradient(45deg, #43a047 30%, #388e3c 90%)"
                  : "linear-gradient(45deg, #66bb6a 30%, #43a047 90%)",
                color: "white",
                fontWeight: "bold",
                padding: "12px 30px",
                borderRadius: "20px",
                textTransform: "none",
                fontSize: "1rem",
                boxShadow: "0 4px 20px rgba(76,175,80,0.4)",
              }}
            >
              Check-In QR-Code {showQR ? "ausblenden" : "zeigen"}
            </Button>
            {showQR && (
              <Paper sx={{ p: 3, mt: 2, display: "inline-block", bgcolor: "white", borderRadius: 3 }}>
                {qrValue ? (
                  <>
                    <QRCodeSVG value={qrValue} size={220} level="M" />
                    <Typography variant="body2" sx={{ mt: 1, color: "#333", fontWeight: 600 }}>
                      Zeige diesen Code am Eingang
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#999", display: "block" }}>
                      Code aktualisiert sich automatisch
                    </Typography>
                  </>
                ) : (
                  <CircularProgress size={24} />
                )}
              </Paper>
            )}
          </Box>
        )}

        <Typography variant="h5" align="center" color="white" gutterBottom>
          {latestAssignment
            ? `Deine naechste Einteilung ist am ${latestAssignment.day}`
            : "Keine aktuelle Einteilung vorhanden"}
        </Typography>

        {/* Einteilung Card */}
        {config.showAssignment !== false && latestAssignment && (
          <Paper elevation={6} sx={{ padding: 3, mt: 3 }}>
            <Typography variant="h6" align="center">Court: {latestAssignment.court}</Typography>
            <Typography variant="h6" align="center">Crew: {latestAssignment.crew}</Typography>

            <Divider sx={{ my: 2 }} />
            {config.showCrewTimes && crewTimes && crewTimes.some(time => time && !time.startsWith("00")) && (
              <>
                <Typography variant="subtitle1" align="center" sx={{ mb: 2 }}>Deine Einsatzzeiten:</Typography>
                <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1 }}>
                  {crewTimes.filter(time => time && !time.startsWith("00")).map((time, index) => (
                    <Chip key={index} icon={<AccessTimeIcon style={{ color: "white" }} />} label={time} sx={{ bgcolor: "#1976d2", color: "white", mb: 1 }} />
                  ))}
                </Box>
              </>
            )}
          </Paper>
        )}

        {/* Custom Announcement */}
        {config.customAnnouncementEnabled && config.customAnnouncement && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(255,255,255,0.12)" }}>
            <Typography variant="body1" align="center" color="white">{config.customAnnouncement}</Typography>
          </Paper>
        )}

        {/* Freikarten-Info */}
        {config.showFreikarten && config.freikartenText && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(255,255,255,0.10)" }}>
            <Typography variant="body1" align="center" color="white">{config.freikartenText}</Typography>
          </Paper>
        )}

        {/* Billing / Abrechnung */}
        {config.showBilling && billingInfo && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(76,175,80,0.15)", border: "1px solid rgba(76,175,80,0.3)" }}>
            <Typography variant="h6" align="center" color="white" gutterBottom>Deine Abrechnung</Typography>
            {billingInfo.totalDays != null && (
              <Typography variant="body1" align="center" color="white">Einsatztage: {billingInfo.totalDays}</Typography>
            )}
            {billingInfo.totalAmount != null && (
              <Typography variant="h5" align="center" color="#66bb6a" sx={{ fontWeight: 700, mt: 1 }}>
                {billingInfo.totalAmount.toFixed(2)} EUR
              </Typography>
            )}
            {billingInfo.note && (
              <Typography variant="body2" align="center" color="white" sx={{ mt: 1, opacity: 0.8 }}>{billingInfo.note}</Typography>
            )}
          </Paper>
        )}

        {/* Meeting Point */}
        {config.meetingPointEnabled && config.meetingPointText && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(255,255,255,0.08)" }}>
            <Typography variant="body1" align="center" color="white">{config.meetingPointText}</Typography>
          </Paper>
        )}

        {/* Crew Times Link */}
        {config.crewTimesLinkEnabled && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Link to={`/${groupSlug || getActiveGroupSlug()}/crew-times`} style={{ color: "#90caf9", fontWeight: "bold", textDecoration: "none" }}>
              Einsatzzeiten aller Crews ansehen
            </Link>
          </Box>
        )}

        {/* Custom Links */}
        {(config.customLinks || []).filter((l) => l.enabled && l.text && l.url).map((link, i) => (
          <Box key={i} sx={{ textAlign: "center", mt: 2 }}>
            <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: "#90caf9", fontWeight: "bold", textDecoration: "none" }}>
              {link.text}
            </a>
          </Box>
        ))}

        {/* Abstimmungen (eingeklappt per default) */}
        {polls.length > 0 && (
          <CollapsibleSection title="Abstimmungen" defaultOpen={false} color="#7c4dff">
            {polls.map((poll) => (
              <PollVote key={poll.id} poll={poll} memberId={id} />
            ))}
          </CollapsibleSection>
        )}

        {/* ATP Link */}
        {config.atpLinkEnabled && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              component="a"
              href={config.atpLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              endIcon={<OpenInNewIcon />}
              sx={{
                background: "linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)",
                color: "white", fontWeight: "bold", padding: "10px 25px", borderRadius: "20px",
                textTransform: "none", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                transition: "transform 0.2s ease-in-out",
                "&:hover": { transform: "scale(1.05)", background: "linear-gradient(45deg, #1e88e5 30%, #1de9b6 90%)" },
              }}
            >
              {config.atpLinkText}
            </Button>
          </Box>
        )}

        {/* Impressum */}
        {config.showImpressum !== false && (
          <Typography variant="caption" align="center" color="white" display="block" sx={{ mt: 4 }}>
            Hinweis: Diese Website ist ein unabhaengiges Projekt und steht in keiner Verbindung zu den Veranstaltern der BMW Open by bitpanda.
            Weitere Informationen findest du im{" "}
            <a href={config.impressumUrl || "http://zilker-solutions.de/impressum-datenschutz-ballkid-management"} style={{ color: "#90caf9", textDecoration: "underline" }}>
              Impressum & Datenschutzhinweis
            </a>.
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default BallkindPage;
